import React, { useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { API_BASE_URL } from 'config';
import { USER_TOKEN, USER_VIEW_MODE } from 'helpers/storage';
import {
  setOpenModal,
  setContent,
  getEditorValue,
  setEditorValue,
  setAiContext,
} from 'store/ai-modal';
import { useDispatch, useSelector } from 'react-redux';

const JoditEditor = dynamic(() => import('jodit-react'), {
  ssr: false,
});

const CustomEditorMCE = ({
  editorState = '',
  height = 600,
  width = '100%',
  setEditorState = () => {},
  placeholder = '',
  disabled = false,
}) => {
  const editor = useRef(null);
  const dispatch = useDispatch();
  const editorValueFromRedux = useSelector(getEditorValue);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);

    return () => {
      setIsClient(false);
    };
  }, []);

  /**
   * @type {IJodit.IJodit}
   */
  const config = useMemo(
    () => ({
      // toolbarAdaptive: true,
      // toolbarButtonSize: 'small',
      readonly: disabled,
      height: height,
      width: width,
      placeholder: placeholder,
      toolbarAdaptive: false,
      toolbarSticky: false,
      toolbarButtonSize: 'small',
      uploader: {
        insertImageAsBase64URI: false,
        url: `${API_BASE_URL}/users-file?type=editor`,
        headers: {
          Authorization: USER_TOKEN.get(),
          Role: USER_VIEW_MODE.get(),
        },
        prepareData: (formData) => {
          const file = formData.getAll('files[0]')[0];
          formData.delete('files[0]');
          formData.append('users-file', file);
          return formData;
        },
        isSuccess: (resp) => {
          return resp && typeof resp.location === 'string';
        },
        getMessage: (resp) => {
          return resp?.location || 'Upload failed';
        },
        process: (resp) => {
          return {
            files: [resp.location.split('/').pop()],
            isImages: [true],
            baseurl: resp.location.replace(/\/[^/]+$/, '/'),
          };
        },
      },
      buttons: [
        'undo',
        'redo',
        '|',
        'fontsize',
        '|',
        'bold',
        'italic',
        '|',
        'brush',
        'underline',
        '|',
        'left',
        'center',
        'right',
        'justify',
        '|',
        'ul',
        'ol',
        '|',
        'link',
        {
          name: 'aiButton',
          icon: `<svg class="custom-ai-assistan-icon" fill="#FA5252" xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 24 24" width="24px" height="24px"><path d="M12,0C11.743,6.518,6.518,11.743,0,12c6.518,0.257,11.743,5.482,12,12c0.257-6.518,5.482-11.743,12-12	C17.482,11.743,12.257,6.518,12,0z M12,17.163C10.743,15.039,8.961,13.257,6.837,12C8.961,10.743,10.743,8.961,12,6.837	c1.257,2.124,3.039,3.906,5.163,5.163C15.039,13.257,13.257,15.039,12,17.163z"/></svg>`,
          exec: (editorInstance) => {
            const editorDoc = editorInstance.editorDocument;
            let selectedHtml = '';

            if (editorDoc) {
              const selection = editorDoc.getSelection();
              if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                const container = document.createElement('div');
                container.appendChild(range.cloneContents());
                selectedHtml = container.innerHTML.trim();
              }
            }
            const fullHtml = editorInstance.value || '';
            const contentToStore =
              selectedHtml !== '' ? selectedHtml : fullHtml;
            dispatch(setOpenModal(true));
            dispatch(setContent(contentToStore));
            dispatch(
              setAiContext({
                full: fullHtml,
                selectedHtml: selectedHtml || null,
              }),
            );
          },
        },
        'image',
        'video',
        '|',
        'preview',
      ],
      // Events added here
      events: {
        afterInit: (editorInstance) => {
          editor.current = { editor: editorInstance };
        },
        change: (newContent) => {
          setEditorState(newContent);
        },
      },
    }),
    [disabled, height, width, placeholder],
  );
  useEffect(() => {
    if (editorValueFromRedux && editor.current?.editor) {
      editor.current.editor.value = editorValueFromRedux;
      setEditorState(editorValueFromRedux);
      dispatch(setEditorValue(null));
    }
  }, [editorValueFromRedux]);
  return isClient ? (
    <>
      <JoditEditor
        editorRef={editor}
        // ref={editor}
        value={editorState}
        config={config}
        onChange={(newContent) => setEditorState(newContent)}
      />
    </>
  ) : null; // or <Skeleton /> if you want a loader
};

export default CustomEditorMCE;
